import React, { useEffect, useMemo, useState } from 'react';

import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { ColumnState, CsvExportParams, GridApi } from 'ag-grid-community';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { FilterInput } from 'src/components/FilterInput';
import { IRateCardDetailStepProps } from 'src/types/IRateCard';
import { getCurrentDate } from 'src/util/dateTimeUtil';

import { fullRatesColDefs } from './fullRatesColDefs';
import { populateFullRateRowData } from './populateFullRateRowData';

export const FullRates: React.FC<IRateCardDetailStepProps> = ({
    rateCardValues,
    setRateCardValues,
    planNameToMetadataObj,
    isEditMode,
    augmentedMetadata,
    dataLoaded
}: IRateCardDetailStepProps) => {
    const [filterText, setFilterText] = useState('');
    const [allTerritoriesFilterValue, launchedTerritoriesFilterValue] = ['all', 'launched'];
    const [territoryFilter, SetTerritoryFilter] = useState(allTerritoriesFilterValue);
    const [agGridApi, setAgGridApi] = useState<GridApi>();

    const rateGridSortModel: ColumnState[] = [{ colId: 'territory', sort: 'asc', sortIndex: 0 }];

    const launchedTerritoryCodeList = useMemo(
        () =>
            augmentedMetadata!.activeServiceLaunchDateMapping![rateCardValues.serviceId].map(
                (territory) => territory.territoryCode
            ),
        [augmentedMetadata!.activeServiceLaunchDateMapping]
    );

    // In edit mode, calculate final full rates
    useEffect(() => {
        if (!isEditMode) {
            return;
        }
        const updatedFullRates = populateFullRateRowData(rateCardValues, planNameToMetadataObj!, augmentedMetadata!);

        setRateCardValues((prevRateCardValue) => ({ ...prevRateCardValue, fullRates: updatedFullRates }));
    }, []);

    useEffect(() => {
        const territoryFilterModel = {
            territory: {
                filterType: 'set',
                values: territoryFilter === launchedTerritoriesFilterValue ? launchedTerritoryCodeList : null
            }
        };
        agGridApi?.setFilterModel(territoryFilterModel);
    }, [territoryFilter]);

    const exportFullRates = () => {
        const params: CsvExportParams = {
            fileName: `full_rates_export_${getCurrentDate()}.csv`
        };
        agGridApi?.exportDataAsCsv(params);
    };

    return (
        <Column width="100%" height="100%" minHeight={480} spacing="300">
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <Row>
                        <ButtonGroup type="outline" size="small" value={territoryFilter} onChange={SetTerritoryFilter}>
                            <ButtonOption value={allTerritoriesFilterValue}>All</ButtonOption>
                            <ButtonOption value={launchedTerritoriesFilterValue}>Launched</ButtonOption>
                        </ButtonGroup>
                        <Button type="link" size="small" onClick={exportFullRates}>
                            <Icon tokens={downloadLargeTokens} />
                            Download
                        </Button>
                    </Row>
                </Row>
            </Box>
            <BaseAgGrid
                columnDefs={fullRatesColDefs}
                rowData={rateCardValues.fullRates}
                filterText={filterText}
                setAgGridApi={setAgGridApi}
                suppressSizeColumnsToFit={true}
                sortModel={rateGridSortModel}
                loading={!dataLoaded}
            />
        </Column>
    );
};
